import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  X,
  CalendarDays,
  Clock,
  ChevronRight,
  CheckCircle2,
  Loader2,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Landmark,
  CreditCard,
  Tag,
  XCircle,
} from 'lucide-react';
import { lessons } from '../mock';
import { useToast } from '../hooks/use-toast';
import { formatApiError } from '../lib/apiError';




const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Group slots returned by the backend by the user's LOCAL calendar date.
// The backend groups by UTC date, but a 22:00 UTC slot belongs to the next
// local day for many users — we want what the visitor actually sees.
const regroupByLocalDate = (apiDays) => {
  const buckets = new Map();
  (apiDays || []).forEach((d) => {
    (d.slots || []).forEach((slot) => {
      const dt = new Date(slot.start_time);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
        2,
        '0'
      )}-${String(dt.getDate()).padStart(2, '0')}`;
      if (!buckets.has(key)) buckets.set(key, { date: key, dateObj: dt, slots: [] });
      buckets.get(key).slots.push({ ...slot, _dt: dt });
    });
  });
  // Sort by date, then slots by time
  return [...buckets.values()]
    .sort((a, b) => a.dateObj - b.dateObj)
    .map((bucket) => ({
      ...bucket,
      slots: bucket.slots.sort((a, b) => a._dt - b._dt),
    }));
};



const formatLocalTime = (dt) =>
  dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });

const formatLongDate = (dt) =>
  dt.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

const BookingModal = ({ open, onClose, initialLessonId = null }) => {
  const [step, setStep] = useState(1);
  const [lesson, setLesson] = useState(lessons[1]);

  // The slot the user has chosen. `null` until step 2.
  // Shape: { start_time: ISO, scheduling_url: string, dateObj: Date }
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Date the user clicked on the day grid (for showing the times list).
  // Shape: same as one entry from regroupByLocalDate.
  const [activeDay, setActiveDay] = useState(null);

  const [info, setInfo] = useState({ name: '', email: '', rank: '' });

  // 'bank_transfer' | 'online_payment' — selected on the new step 3
  const [paymentMethod, setPaymentMethod] = useState(null);

  // Discount code entry & application state (step 3)
  const [discountInput, setDiscountInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null); // { code, percent, description }
  const [discountError, setDiscountError] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  // Availability data
  const [availability, setAvailability] = useState(null); // grouped local-date array
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);

  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [bookingResult, setBookingResult] = useState(null); // { prefilled_url, id }

  const { toast } = useToast();

  const fetchAvailability = useCallback(
    async (lessonId, paymentMethodArg = 'bank_transfer', discountPercentArg = 0) => {
      setLoadingAvailability(true);
      setAvailabilityError(null);
      setAvailability(null);
      try {
        const { data } = await axios.get(`${API}/calendly/availability`, {
          params: {
            lesson_id: lessonId,
            payment_method: paymentMethodArg,
            discount_percent: discountPercentArg,
          },
          timeout: 20000,
        });
        const grouped = regroupByLocalDate(data?.available_days || []);
        setAvailability(grouped);
        return grouped;
      } catch (err) {
        setAvailabilityError(formatApiError(err, 'Could not load available time slots.'));
        throw err;
      } finally {
        setLoadingAvailability(false);
      }
    },
    // All referenced identifiers (axios, API, regroupByLocalDate, set* fns)
    // are stable module-level imports / React state setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Try to apply the code the user typed into the discount input.
  // Validates against the backend so codes are truly single-use across all
  // users. The actual consumption happens atomically in /api/calendly/book.
  const applyDiscount = async () => {
    const raw = discountInput.trim().toUpperCase();
    if (!raw) {
      setDiscountError('Please enter a code.');
      setAppliedDiscount(null);
      return;
    }
    setValidatingDiscount(true);
    setDiscountError(null);
    try {
      const { data } = await axios.post(
        `${API}/discount/validate`,
        { code: raw },
        { timeout: 15000 }
      );
      setAppliedDiscount({
        code: data.code,
        percent: data.percent,
        description: data.description || '',
      });
      toast({
        title: `Discount applied — ${data.percent}% off`,
        description: data.description || '',
      });
    } catch (err) {
      const status = err?.response?.status;
      const fallback =
        status === 410
          ? 'This code has already been used.'
          : status === 404
          ? 'This code is not valid.'
          : 'Could not validate the code. Please try again.';
      setDiscountError(formatApiError(err, fallback));
      setAppliedDiscount(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountInput('');
    setDiscountError(null);
  };

  // Effective price after discount (kept as strings for display).
  const priceInfo = useMemo(() => {
    const original = Number(lesson?.price || 0);
    if (!appliedDiscount) {
      return {
        original,
        effective: original,
        savings: 0,
        percent: 0,
      };
    }
    const effective = Math.max(
      0,
      Math.round(original * (1 - appliedDiscount.percent / 100) * 100) / 100
    );
    return {
      original,
      effective,
      savings: Math.round((original - effective) * 100) / 100,
      percent: appliedDiscount.percent,
    };
  }, [lesson, appliedDiscount]);

  // Reset & initial state when the modal opens
  useEffect(() => {
    if (!open) return;
    setSelectedSlot(null);
    setActiveDay(null);
    setInfo({ name: '', email: '', rank: '' });
    setPaymentMethod(null);
    setDiscountInput('');
    setAppliedDiscount(null);
    setDiscountError(null);

    if (initialLessonId != null) {
      const found = lessons.find((l) => l.id === initialLessonId);
      if (found) {
        setLesson(found);
        setStep(2);
        return;
      }
    }
    setLesson(lessons[1]);
    setStep(1);
  }, [open, initialLessonId]);

  // Fetch availability whenever we enter step 2 with a known lesson
  useEffect(() => {
    if (!open) return;
    if (step !== 2) return;
    if (!lesson?.id) return;
    // Clear any previous selection — slots belong to a specific event type
    setSelectedSlot(null);
    setActiveDay(null);
    fetchAvailability(lesson.id);
  }, [open, step, lesson?.id, fetchAvailability]);

  // Auto-select first day with slots when availability arrives
  useEffect(() => {
    if (availability && availability.length > 0 && !activeDay) {
      setActiveDay(availability[0]);
    }
  }, [availability, activeDay]);

  const reset = () => {
    setStep(1);
    setLesson(lessons[1]);
    setSelectedSlot(null);
    setActiveDay(null);
    setInfo({ name: '', email: '', rank: '' });
    setPaymentMethod(null);
    setDiscountInput('');
    setAppliedDiscount(null);
    setDiscountError(null);
    setAvailability(null);
    setAvailabilityError(null);
    setSubmittingBooking(false);
    setBookingResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const confirm = async () => {
    if (!selectedSlot || !info.name || !info.email) return;
    setSubmittingBooking(true);
    try {
      const { data } = await axios.post(
        `${API}/calendly/book`,
        {
          lesson_id: lesson.id,
          scheduling_url: selectedSlot.scheduling_url,
          start_time: selectedSlot.start_time,
          name: info.name,
          email: info.email,
          rank: info.rank || '',
          payment_method: paymentMethod || 'bank_transfer',
          discount_code: appliedDiscount ? appliedDiscount.code : '',
          discount_percent: appliedDiscount ? appliedDiscount.percent : 0,
        },
        { timeout: 20000 }
      );
      setBookingResult({ prefilled_url: data.prefilled_url, id: data.id });
      // Open Calendly in a new tab so the user can finalize the booking
      try {
        window.open(data.prefilled_url, '_blank', 'noopener,noreferrer');
      } catch (popupErr) {
        // Browser blocked the new tab — log for visibility; the success
        // screen also shows the link as a button so the user can still click.
        console.warn('Could not open Calendly tab automatically:', popupErr);
      }
      setStep(5);
    } catch (err) {
      // Race condition: another user consumed the code between validate & book.
      // Send the user back to step 3 so they can pick a different code or skip.
      if (err?.response?.status === 409) {
        setAppliedDiscount(null);
        setDiscountInput('');
        setDiscountError('This code was just used by someone else. Please try another.');
        setStep(3);
        toast({
          title: 'Discount code no longer available',
          description:
            'It was used before you could confirm. Please pick another code or continue without one.',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Booking failed',
        description: formatApiError(err, 'Could not create your booking. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setSubmittingBooking(false);
    }
  };

  // Called when leaving the payment step. If the user picked Online Payment we
  // need to re-fetch availability from the payment calendar and remap the
  // selected slot to a scheduling_url tied to that event type (otherwise the
  // booking link would still point to the bank-transfer flow).
  const advanceFromPaymentStep = async () => {
    if (!paymentMethod || !selectedSlot) return;

    // Bank transfer = nothing to remap, current scheduling_url is correct.
    if (paymentMethod === 'bank_transfer') {
      setStep(4);
      return;
    }

    // Online payment — swap to the payment calendar's matching slot.
    // Pass the discount percent so we hit the discounted-price event type when applicable.
    try {
      const discountPct = appliedDiscount ? Math.floor(appliedDiscount.percent) : 0;
      const grouped = await fetchAvailability(lesson.id, 'online_payment', discountPct);
      const targetStart = selectedSlot.start_time;
      let match = null;
      for (const day of grouped) {
        for (const slot of day.slots) {
          if (slot.start_time === targetStart) {
            match = slot;
            break;
          }
        }
        if (match) break;
      }
      if (!match) {
        toast({
          title: 'Slot unavailable',
          description:
            'That time is not available for online payment. Please pick a different slot.',
          variant: 'destructive',
        });
        setStep(2);
        return;
      }
      // Replace selectedSlot with the payment-calendar variant
      setSelectedSlot({
        start_time: match.start_time,
        scheduling_url: match.scheduling_url,
        dateObj: match._dt,
      });
      setStep(4);
    } catch (_e) {
      // fetchAvailability already set availabilityError + toast not needed —
      // surface a clear failure message anyway.
      toast({
        title: 'Could not switch calendar',
        description:
          'Failed to load the online-payment calendar. Please try again or use bank transfer.',
        variant: 'destructive',
      });
    }
  };

  const slotsForActiveDay = useMemo(() => {
    if (!activeDay) return [];
    return activeDay.slots;
  }, [activeDay]);

  const tzLabel = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (_e) {
      return 'local time';
    }
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0d0d0d] border border-amber-400/30 rounded-2xl shadow-2xl">
        <div className="sticky top-0 bg-[#0d0d0d] z-10 flex items-center justify-between px-7 py-5 border-b border-white/10">
          <div>
            <p className="text-amber-400 text-[11px] tracking-[0.32em] font-medium">BOOKING</p>
            <h3 className="font-serif text-white text-2xl mt-1">
              {step === 5 ? 'You are all set!' : 'Book Your Lesson'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full hover:bg-white/5 flex items-center justify-center text-white/70 hover:text-amber-400 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {step !== 5 && (
          <div className="px-7 pt-5 flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div
                  className={`h-1.5 flex-1 rounded-full ${
                    s <= step ? 'bg-amber-400' : 'bg-white/10'
                  }`}
                />
              </div>
            ))}
          </div>
        )}

        <div className="p-7">
          {/* STEP 1 — Choose lesson */}
          {step === 1 && (
            <>
              <p className="text-white/60 text-sm mb-5">Step 1 — Choose your lesson</p>
              <div className="space-y-3">
                {lessons.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setLesson(l)}
                    className={`w-full text-left p-5 rounded-xl border transition-colors ${
                      lesson.id === l.id
                        ? 'border-amber-400 bg-amber-400/[0.06]'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-white font-serif text-xl">{l.name}</div>
                        <div className="text-white/50 text-sm mt-1 flex items-center gap-2">
                          <Clock size={13} /> {l.duration}
                        </div>
                      </div>
                      <div className="text-amber-400 font-serif text-3xl">{l.price}€</div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-7 flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors font-medium"
                >
                  Next <ChevronRight size={18} />
                </button>
              </div>
            </>
          )}

          {/* STEP 2 — Pick date & time (live from Calendly) */}
          {step === 2 && (
            <>
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <p className="text-white/60 text-sm">Step 2 — Pick a date &amp; time</p>
                  <p className="text-white/40 text-xs mt-1">
                    Times shown in your timezone ({tzLabel})
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-white/40 text-[10px] uppercase tracking-wider">Lesson</div>
                  <div className="text-amber-400 text-sm font-medium">{lesson.name}</div>
                </div>
              </div>

              {/* Loading */}
              {loadingAvailability && (
                <div className="py-16 flex flex-col items-center justify-center text-white/60">
                  <Loader2 size={28} className="text-amber-400 animate-spin" />
                  <p className="mt-4 text-sm">Loading available slots…</p>
                </div>
              )}

              {/* Error */}
              {!loadingAvailability && availabilityError && (
                <div className="py-10 px-6 rounded-xl border border-red-400/30 bg-red-400/[0.04]">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-red-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">
                        Could not load availability
                      </p>
                      <p className="text-white/55 text-sm mt-1">{availabilityError}</p>
                      <button
                        onClick={() => fetchAvailability(lesson.id)}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-400/50 text-amber-400 hover:bg-amber-400 hover:text-[#0a0a0a] text-sm transition-colors"
                      >
                        <RefreshCw size={14} /> Try again
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty */}
              {!loadingAvailability &&
                !availabilityError &&
                availability &&
                availability.length === 0 && (
                  <div className="py-10 px-6 rounded-xl border border-white/10 bg-white/[0.02] text-center">
                    <p className="text-white text-sm font-medium">No open slots this week</p>
                    <p className="text-white/55 text-sm mt-2">
                      All slots for the next 7 days are booked. Please check back soon or contact
                      me directly.
                    </p>
                  </div>
                )}

              {/* Slots */}
              {!loadingAvailability &&
                !availabilityError &&
                availability &&
                availability.length > 0 && (
                  <>
                    <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                      {availability.map((day) => {
                        const isActive = activeDay?.date === day.date;
                        return (
                          <button
                            key={day.date}
                            onClick={() => setActiveDay(day)}
                            className={`p-3 rounded-xl border text-center transition-colors ${
                              isActive
                                ? 'border-amber-400 bg-amber-400/[0.08] text-amber-400'
                                : 'border-white/10 text-white/80 hover:border-white/30'
                            }`}
                          >
                            <div className="text-[11px] uppercase tracking-wider opacity-70">
                              {day.dateObj.toLocaleDateString(undefined, { weekday: 'short' })}
                            </div>
                            <div className="font-serif text-xl mt-1">
                              {day.dateObj.getDate()}
                            </div>
                            <div className="text-[10px] mt-1 opacity-60">
                              {day.slots.length} slot{day.slots.length === 1 ? '' : 's'}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {activeDay && (
                      <>
                        <div className="mt-7 text-white/60 text-sm mb-3">
                          Available times — {formatLongDate(activeDay.dateObj)}
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {slotsForActiveDay.map((slot) => {
                            const isSelected =
                              selectedSlot?.start_time === slot.start_time;
                            return (
                              <button
                                key={slot.start_time}
                                onClick={() =>
                                  setSelectedSlot({
                                    start_time: slot.start_time,
                                    scheduling_url: slot.scheduling_url,
                                    dateObj: slot._dt,
                                  })
                                }
                                className={`px-4 py-3 rounded-lg border transition-colors ${
                                  isSelected
                                    ? 'border-amber-400 bg-amber-400/[0.08] text-amber-400'
                                    : 'border-white/10 text-white/80 hover:border-white/30'
                                }`}
                              >
                                {formatLocalTime(slot._dt)}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                )}

              <div className="mt-7 flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 rounded-full border border-white/20 text-white/80 hover:border-amber-400 hover:text-amber-400 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!selectedSlot}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight size={18} />
                </button>
              </div>
            </>
          )}

          {/* STEP 3 — Payment method */}
          {step === 3 && (
            <>
              <p className="text-white/60 text-sm mb-5">Step 3 — How will you pay?</p>

              <div className="grid sm:grid-cols-2 gap-4">
                {/* Online Payment */}
                <button
                  type="button"
                  onClick={() => setPaymentMethod('online_payment')}
                  className={`text-left p-6 rounded-xl border transition-all duration-200 ${
                    paymentMethod === 'online_payment'
                      ? 'border-amber-400 bg-amber-400/[0.06]'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 border ${
                      paymentMethod === 'online_payment'
                        ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                        : 'border-white/15 text-white/70'
                    }`}
                  >
                    <CreditCard size={20} />
                  </div>
                  <div className="text-white font-serif text-xl">Online Payment</div>
                  <p className="text-white/55 text-sm mt-2 leading-relaxed">
                    Pay instantly with card or PayPal on the next step. Your lesson is
                    confirmed immediately.
                  </p>
                  <p className="text-amber-400 text-[11px] tracking-[0.2em] uppercase mt-4">
                    Instant lesson confirmation
                  </p>
                </button>

                {/* Bank Transfer */}
                <button
                  type="button"
                  onClick={() => setPaymentMethod('bank_transfer')}
                  className={`text-left p-6 rounded-xl border transition-all duration-200 ${
                    paymentMethod === 'bank_transfer'
                      ? 'border-amber-400 bg-amber-400/[0.06]'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 border ${
                      paymentMethod === 'bank_transfer'
                        ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                        : 'border-white/15 text-white/70'
                    }`}
                  >
                    <Landmark size={20} />
                  </div>
                  <div className="text-white font-serif text-xl">Bank Transfer</div>
                  <p className="text-white/55 text-sm mt-2 leading-relaxed">
                    Pay manually after the lesson is confirmed. You will receive the IBAN
                    details by email.
                  </p>
                  <p className="text-amber-400 text-[11px] tracking-[0.2em] uppercase mt-4">
                    Confirmation after payment
                  </p>
                </button>
              </div>

              {/* Discount code */}
              <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Tag size={14} className="text-amber-400" />
                  <span className="text-white/80 text-sm font-medium">
                    Have a discount code?
                  </span>
                  <span className="text-white/40 text-xs">optional</span>
                </div>

                {appliedDiscount ? (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-400/40 bg-amber-400/[0.06] px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <CheckCircle2 size={16} className="text-amber-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-white text-sm font-medium truncate">
                          <span className="text-amber-400">{appliedDiscount.code}</span>
                          <span className="text-white/70"> — {appliedDiscount.percent}% off</span>
                        </div>
                        <div className="text-white/50 text-xs truncate">
                          {appliedDiscount.description}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeDiscount}
                      className="text-white/60 hover:text-amber-400 shrink-0 transition-colors"
                      aria-label="Remove discount"
                    >
                      <XCircle size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={discountInput}
                      onChange={(e) => {
                        setDiscountInput(e.target.value);
                        if (discountError) setDiscountError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyDiscount();
                        }
                      }}
                      placeholder="Enter code"
                      className="flex-1 bg-transparent border border-white/15 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 uppercase tracking-wider focus:outline-none focus:border-amber-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={applyDiscount}
                      disabled={!discountInput.trim() || validatingDiscount}
                      className="px-5 py-2.5 rounded-lg border border-amber-400/50 text-amber-400 hover:bg-amber-400 hover:text-[#0a0a0a] transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    >
                      {validatingDiscount ? (
                        <>
                          <Loader2 size={14} className="animate-spin" /> Checking
                        </>
                      ) : (
                        'Apply'
                      )}
                    </button>
                  </div>
                )}

                {discountError && (
                  <p className="mt-3 text-red-400 text-xs flex items-center gap-1.5">
                    <AlertCircle size={12} /> {discountError}
                  </p>
                )}

                {appliedDiscount && (
                  <div className="mt-3 flex items-baseline justify-between text-sm">
                    <span className="text-white/60">New price</span>
                    <span className="text-white">
                      <span className="line-through text-white/40 mr-2">
                        {priceInfo.original}€
                      </span>
                      <span className="text-amber-400 font-serif text-xl">
                        {priceInfo.effective}€
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-7 flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  disabled={loadingAvailability}
                  className="px-6 py-3 rounded-full border border-white/20 text-white/80 hover:border-amber-400 hover:text-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={advanceFromPaymentStep}
                  disabled={!paymentMethod || loadingAvailability}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loadingAvailability ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Checking…
                    </>
                  ) : (
                    <>
                      Next <ChevronRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {/* STEP 4 — Your details */}
          {step === 4 && (
            <>
              <p className="text-white/60 text-sm mb-5">Step 4 — Your details</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-white/55 text-xs uppercase tracking-wider mb-2">
                    Full Name
                  </label>
                  <input
                    value={info.name}
                    onChange={(e) => setInfo({ ...info, name: e.target.value })}
                    className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-white/55 text-xs uppercase tracking-wider mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={info.email}
                    onChange={(e) => setInfo({ ...info, email: e.target.value })}
                    className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="block text-white/55 text-xs uppercase tracking-wider mb-2">
                    Current Rank (optional)
                  </label>
                  <input
                    placeholder="e.g. 8 kyu on OGS"
                    value={info.rank}
                    onChange={(e) => setInfo({ ...info, rank: e.target.value })}
                    className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>

              <div className="mt-6 p-5 rounded-xl border border-amber-400/30 bg-amber-400/[0.05]">
                <div className="text-white/55 text-xs uppercase tracking-wider mb-3">Summary</div>
                <div className="flex items-center justify-between text-white">
                  <span>
                    {lesson.name} · {lesson.duration}
                  </span>
                  <span className="text-amber-400 font-serif text-xl">
                    {appliedDiscount ? (
                      <>
                        <span className="line-through text-white/40 text-sm mr-2 font-sans">
                          {priceInfo.original}€
                        </span>
                        {priceInfo.effective}€
                      </>
                    ) : (
                      <>{lesson.price}€</>
                    )}
                  </span>
                </div>
                {selectedSlot && (
                  <div className="text-white/65 text-sm mt-2 flex items-center gap-2">
                    <CalendarDays size={14} className="text-amber-400" />
                    {formatLongDate(selectedSlot.dateObj)} at{' '}
                    {formatLocalTime(selectedSlot.dateObj)}
                  </div>
                )}
                {paymentMethod && (
                  <div className="text-white/65 text-sm mt-2 flex items-center gap-2">
                    {paymentMethod === 'online_payment' ? (
                      <CreditCard size={14} className="text-amber-400" />
                    ) : (
                      <Landmark size={14} className="text-amber-400" />
                    )}
                    {paymentMethod === 'online_payment'
                      ? 'Online Payment'
                      : 'Bank Transfer'}
                  </div>
                )}
                {appliedDiscount && (
                  <div className="text-white/65 text-sm mt-2 flex items-center gap-2">
                    <Tag size={14} className="text-amber-400" />
                    <span>
                      <span className="text-amber-400 font-medium">
                        {appliedDiscount.code}
                      </span>{' '}
                      — {appliedDiscount.percent}% off (saved{' '}
                      {priceInfo.savings}€)
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-7 flex justify-between">
                <button
                  onClick={() => setStep(3)}
                  className="px-6 py-3 rounded-full border border-white/20 text-white/80 hover:border-amber-400 hover:text-amber-400 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={confirm}
                  disabled={
                    !info.name || !info.email || !selectedSlot || submittingBooking
                  }
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submittingBooking ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Confirming…
                    </>
                  ) : (
                    'Confirm Booking'
                  )}
                </button>
              </div>
            </>
          )}

          {/* STEP 5 — Done (redirected to Calendly) */}
          {step === 5 && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-amber-400/15 border border-amber-400/50 flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} className="text-amber-400" />
              </div>
              <h4 className="mt-6 font-serif text-white text-3xl">
                One last step on Calendly
              </h4>
              <p className="mt-3 text-white/65 max-w-md mx-auto leading-relaxed">
                I just opened a Calendly tab pre-filled with your details. Click{' '}
                <span className="text-amber-400 font-medium">Confirm</span> there to finalize
                your{' '}
                <span className="text-white font-medium">
                  {lesson.name}
                </span>
                {selectedSlot && (
                  <>
                    {' '}on{' '}
                    <span className="text-white font-medium">
                      {formatLongDate(selectedSlot.dateObj)} at{' '}
                      {formatLocalTime(selectedSlot.dateObj)}
                    </span>
                  </>
                )}
                . Calendly will email the meeting link to{' '}
                <span className="text-amber-400">{info.email}</span>.
              </p>

              {bookingResult?.prefilled_url && (
                <a
                  href={bookingResult.prefilled_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors font-medium"
                >
                  Open Calendly again <ExternalLink size={16} />
                </a>
              )}

              <p className="mt-5 text-white/40 text-xs">
                Tab didn't open? Your browser may have blocked it — use the button above.
              </p>

              <button
                onClick={handleClose}
                className="mt-6 px-7 py-3 rounded-full border border-white/20 text-white/70 hover:border-amber-400 hover:text-amber-400 transition-colors text-sm"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
